import type { Tutorial } from './types'

/** category_type ids used by the site. */
export const CATEGORY_TYPE = {
  instructors: 2,
  skillLevel: 7,
} as const

export function instructorOf(tutorial: Pick<Tutorial, 'categories'>) {
  return tutorial.categories?.find((c) => c.category_type?.id === CATEGORY_TYPE.instructors)
}

export function difficultyOf(tutorial: Pick<Tutorial, 'categories'>): string | undefined {
  return tutorial.categories?.find((c) => c.category_type?.id === CATEGORY_TYPE.skillLevel)?.title
}

export function aboutSnippet(tutorial: Pick<Tutorial, 'snippets'>): string | undefined {
  return tutorial.snippets?.find((s) => s.type === 'about')?.content
}

/** Transcript snippet as HTML (newlines -> <br>), or '' when there is none. */
export function transcriptHtml(tutorial: Pick<Tutorial, 'snippets'>): string {
  const snippet = tutorial.snippets?.find((s) => s.type === 'transcript')
  return snippet ? snippet.content.replace(/\n/g, '<br>') : ''
}

/** Uploaded hero image, falling back to the YouTube max-res thumbnail. */
export function heroImageOf(tutorial: Pick<Tutorial, 'images'>): string | undefined {
  const images = tutorial.images ?? []
  return (images.find((i) => i.type === 'hero') ?? images.find((i) => i.type === 'youtube_maxres'))?.url
}

/** A title as stored, on one line with single spaces (some have double spaces or stray newlines). */
export const cleanTitle = (title: string) => title.replace(/\s+/g, ' ').trim()

/**
 * Heading/card label: the title without a trailing "(…)" note such as "(Free Tutorial)". Parentheses inside the
 * title stay: "Donna's New (very easy) FREE Mosaic Quilt pattern!" is a whole title (v1 cut it at the "(").
 */
export const displayTitle = (title: string) => cleanTitle(title).replace(/\s*\([^()]*\)$/, '') || cleanTitle(title)

/** Breadcrumb label: the display title, max 48 chars. */
export const crumbTitle = (title: string) => displayTitle(title).slice(0, 48).trim()

/** The tutorial's category of a category type, by the type's title ("Fabric Cut", "Theme", …). */
export function categoryOf(tutorial: Pick<Tutorial, 'categories'>, typeTitle: string) {
  return tutorial.categories?.find((c) => c.category_type?.title === typeTitle)
}

/** A category title as the search index stores it (' becomes ", as in v1), for facet filters and links. */
export const facetValueOf = (title: string) => title.replace(/'/g, '"')

/** Facets tried, in order, to find tutorials like this one. */
export const RELATED_FACETS = ['Fabric Cut', 'Project Type', 'Theme'] as const
